'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { WooCommerceProduct } from '@/types/woocommerce';
import { isAuthenticated } from '@/lib/apollo-client-simple';

const WORDPRESS_URL = process.env.NEXT_PUBLIC_WORDPRESS_URL || process.env.WORDPRESS_URL || 'https://demo2.beauty.local';

interface WishlistContextType {
  items: WooCommerceProduct[];
  addToWishlist: (product: WooCommerceProduct) => Promise<void>;
  removeFromWishlist: (productId: number | string) => Promise<void>;
  isInWishlist: (productId: number | string) => boolean;
  clearWishlist: () => Promise<void>;
  itemCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initial state empty on both server and client; localStorage read inside
  // a mount effect for hydration safety (React #418 otherwise when SSR [] and
  // CSR populated-array disagree).
  const [items, setItems] = useState<WooCommerceProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const hasLoadedFromAPI = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || isAuthenticated()) return;
    const savedWishlist = localStorage.getItem('wishlist');
    if (!savedWishlist) return;
    try {
      setItems(JSON.parse(savedWishlist));
    } catch (e) {
      console.error('Failed to load wishlist:', e);
    }
  }, []);

  // Load wishlist from WordPress if user is authenticated
  useEffect(() => {
    if (typeof window !== 'undefined' && isAuthenticated() && !hasLoadedFromAPI.current) {
      hasLoadedFromAPI.current = true;
      loadWishlistFromWordPress();
    }
  }, []);

  // Sync wishlist to WordPress whenever it changes (if authenticated)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isAuthenticated() && items.length > 0 && !syncing && hasLoadedFromAPI.current) {
        syncWishlistToWordPress();
      } else if (!isAuthenticated()) {
        // Fallback to localStorage for non-authenticated users
        localStorage.setItem('wishlist', JSON.stringify(items));
      }
    }
  }, [items]);

  const loadWishlistFromWordPress = useCallback(async () => {
    if (!isAuthenticated()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('auth-token');
      const response = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.products) {
          // Transform WordPress product data to WooCommerceProduct format
          const transformedProducts = data.products.map((product: any) => ({
            id: product.id,
            name: product.name,
            slug: product.slug,
            price: product.price,
            regular_price: product.regular_price,
            sale_price: product.sale_price,
            images: product.image ? [{ src: product.image }] : [],
            // Add other required fields with defaults
            type: 'simple',
            status: 'publish',
            featured: false,
            catalog_visibility: 'visible',
            description: '',
            short_description: '',
            sku: '',
            manage_stock: false,
            stock_quantity: null,
            stock_status: 'instock',
            backorders: 'no',
            backorders_allowed: false,
            backordered: false,
            sold_individually: false,
            weight: '',
            dimensions: { length: '', width: '', height: '' },
            shipping_required: true,
            shipping_taxable: true,
            shipping_class: '',
            shipping_class_id: 0,
            reviews_allowed: true,
            average_rating: '0',
            rating_count: 0,
            related_ids: [],
            upsell_ids: [],
            cross_sell_ids: [],
            parent_id: 0,
            purchase_limit: -1,
            categories: [],
            tags: [],
            attributes: [],
            default_attributes: [],
            variations: [],
            grouped_products: [],
            menu_order: 0,
            meta_data: [],
            date_created: '',
            date_modified: '',
            date_on_sale_from: null,
            date_on_sale_to: null,
            on_sale: false,
            total_sales: 0,
            tax_status: 'taxable',
            tax_class: '',
            virtual: false,
            downloadable: false,
            downloads: [],
            download_limit: -1,
            download_expiry: -1,
            external_url: '',
            button_text: '',
            product_url: '',
            productId: product.id,
          }));
          // Only overwrite local state when the server has items — otherwise
          // an empty server wishlist (common during the sync rollout period)
          // would wipe a user's existing local wishlist on login.
          if (transformedProducts.length > 0) {
            setItems(transformedProducts);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load wishlist from WordPress:', error);
      // Fallback to localStorage
      const savedWishlist = localStorage.getItem('wishlist');
      if (savedWishlist) {
        try {
          setItems(JSON.parse(savedWishlist));
        } catch (e) {
          console.error('Failed to load wishlist from localStorage:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const syncWishlistToWordPress = useCallback(async () => {
    if (!isAuthenticated() || syncing) return;

    setSyncing(true);
    try {
      const token = localStorage.getItem('auth-token');
      // Get current WordPress wishlist
      const response = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const wpProductIds = data.success ? data.products.map((p: any) => p.id) : [];
        const localProductIds = items.map((item: WooCommerceProduct) => {
          // Extract numeric ID from product.id (could be "shop-123" or just 123)
          const idStr = String(item.id);
          const match = idStr.match(/(\d+)$/);
          return match ? parseInt(match[1]) : parseInt(idStr);
        }).filter(id => !isNaN(id));

        // Add products that are in local but not in WordPress
        for (const productId of localProductIds) {
          if (!wpProductIds.includes(productId)) {
            await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/wishlist/add`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ product_id: productId }),
            });
          }
        }

        // Remove products that are in WordPress but not in local
        for (const productId of wpProductIds) {
          if (!localProductIds.includes(productId)) {
            await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/wishlist/remove`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ product_id: productId }),
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to sync wishlist to WordPress:', error);
    } finally {
      setSyncing(false);
    }
  }, [items, syncing]);

  const addToWishlist = useCallback(async (product: WooCommerceProduct) => {
    // Extract numeric product ID
    const productIdStr = String(product.id);
    const match = productIdStr.match(/(\d+)$/);
    const productId = match ? parseInt(match[1]) : parseInt(productIdStr);

    if (isNaN(productId)) {
      console.error('Invalid product ID:', product.id);
      return;
    }

    // Check if already in wishlist
    const exists = items.find((item) => {
      const itemIdStr = String(item.id);
      const itemMatch = itemIdStr.match(/(\d+)$/);
      const itemId = itemMatch ? parseInt(itemMatch[1]) : parseInt(itemIdStr);
      return itemId === productId;
    });

    if (exists) {
      return; // Already in wishlist
    }

    // Add to local state immediately
    setItems((prevItems) => [...prevItems, product]);

    // Sync to WordPress if authenticated
    if (isAuthenticated()) {
      try {
        const token = localStorage.getItem('auth-token');
        const response = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/wishlist/add`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ product_id: productId }),
        });

        if (!response.ok) {
          // Log but keep the item in local state — the server sync may not
          // recognize frontend-generated IDs yet (they don't match WC post
          // IDs until we wire GraphQL-sourced products end-to-end).
          console.warn('Wishlist server sync failed, keeping local state.');
        }
        // Intentionally NOT reloading from server — see loadWishlistFromWordPress
        // comment: an empty server response would wipe local state.
      } catch (error) {
        console.error('Error adding product to wishlist:', error);
        // Keep local state on network error too.
      }
    }
  }, [items, loadWishlistFromWordPress]);

  const removeFromWishlist = useCallback(async (productId: number | string) => {
    // Extract numeric product ID
    const productIdStr = String(productId);
    const match = productIdStr.match(/(\d+)$/);
    const numericProductId = match ? parseInt(match[1]) : parseInt(productIdStr);

    if (isNaN(numericProductId)) {
      console.error('Invalid product ID:', productId);
      return;
    }

    // Remove from local state immediately
    setItems((prevItems) => prevItems.filter((item) => {
      const itemIdStr = String(item.id);
      const itemMatch = itemIdStr.match(/(\d+)$/);
      const itemId = itemMatch ? parseInt(itemMatch[1]) : parseInt(itemIdStr);
      return itemId !== numericProductId;
    }));

    // Sync to WordPress if authenticated
    if (isAuthenticated()) {
      try {
        const token = localStorage.getItem('auth-token');
        const response = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/wishlist/remove`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ product_id: numericProductId }),
        });

        if (!response.ok) {
          console.error('Failed to remove product from wishlist in WordPress');
          // Do NOT reload from server — if the WP wishlist hasn't been synced
          // yet it will return empty and wipe the user's local state. Local
          // state is authoritative after the optimistic filter above.
        }
      } catch (error) {
        console.error('Error removing product from wishlist:', error);
        // Reload wishlist from WordPress on error
        await loadWishlistFromWordPress();
      }
    }
  }, [loadWishlistFromWordPress]);

  const isInWishlist = useCallback((productId: number | string) => {
    const productIdStr = String(productId);
    const match = productIdStr.match(/(\d+)$/);
    const numericProductId = match ? parseInt(match[1]) : parseInt(productIdStr);

    if (isNaN(numericProductId)) {
      return false;
    }

    return items.some((item) => {
      const itemIdStr = String(item.id);
      const itemMatch = itemIdStr.match(/(\d+)$/);
      const itemId = itemMatch ? parseInt(itemMatch[1]) : parseInt(itemIdStr);
      return itemId === numericProductId;
    });
  }, [items]);

  const clearWishlist = useCallback(async () => {
    setItems([]);

    // Clear in WordPress if authenticated
    if (isAuthenticated()) {
      try {
        const token = localStorage.getItem('auth-token');
        await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/wishlist/clear`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Error clearing wishlist:', error);
      }
    }
  }, []);

  // Memoize itemCount calculation
  const itemCount = useMemo(() => items.length, [items]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    items,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    clearWishlist,
    itemCount,
    loading,
    refresh: loadWishlistFromWordPress,
  }), [items, addToWishlist, removeFromWishlist, isInWishlist, clearWishlist, itemCount, loading, loadWishlistFromWordPress]);

  return (
    <WishlistContext.Provider value={contextValue}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
